import { useEffect, useCallback, useMemo } from "react";
import { useInfluencerStore } from "@/features/influencer-monitoring/store/influencerStore";
import { useMatchedInfluencers } from "@/features/influencer-monitoring/api/influencer.queries";
import { useInfluencersApiToken } from "@/features/auth/hooks/useApiToken";
import { useAuthStore } from "@/features/auth/store/useAuthStore";
import { MONITORING_TYPES } from "@/constants/monitoring";
import { generateInfluencerEndpoint } from "@/lib/endpoint";
import { useContainerLaunch } from "@/hooks/useContainerLaunch";
import { useContainerData } from "@/hooks/useContainerData";
import { useTokenManagement } from "@/hooks/useTokenManagement";
import { getLinkValidation } from "@/features/influencer-monitoring/utils/linkValidation";

/**
 * Controller hook for InfluencerContainer
 * Manages all business logic, state, and side effects
 */
export const useInfluencerContainer = () => {
  const state = useInfluencerStore();
  const { influencerData, uploadInfluencersFile, showLinks, setShowLinks } =
    state;

  const {
    apiToken,
    selectedPlatforms: tokenSelectedPlatforms,
    isPending: isApiTokenLoading,
  } = useInfluencersApiToken();
  const { user } = useAuthStore();

  // Launch management
  const {
    showResults,
    hasLaunched,
    handleShowResults,
    handleLaunch: baseLaunch,
    handlePanelResize,
  } = useContainerLaunch();

  // Link validation: invalid URLs and duplicates (needed for query options)
  const linkValidation = useMemo(
    () => getLinkValidation(influencerData),
    [influencerData],
  );

  // Data fetching: only runs when user has clicked Launch (not on add row / cell edit)
  const {
    data: matchedInfluencers = [],
    refetch,
    isFetching,
  } = useMatchedInfluencers(influencerData, {
    hasLaunched,
    linkValidation,
  });

  const { responseJSON, setResponseJSON, handleCopyCode } =
    useContainerData(matchedInfluencers);

  // Update response JSON when data changes
  useEffect(() => {
    if (matchedInfluencers) {
      setResponseJSON(JSON.stringify(matchedInfluencers, null, 2));
    }
  }, [matchedInfluencers, setResponseJSON]);

  // Empty results: no rows or every row has data: [] (no matched posters)
  const isEmptyResults = useMemo(
    () =>
      !matchedInfluencers?.length ||
      matchedInfluencers.every(
        (r: { data?: unknown[] }) =>
          !Array.isArray(r?.data) || r.data.length === 0,
      ),
    [matchedInfluencers],
  );

  // Check if data has links
  const hasLinks = useMemo(
    () =>
      influencerData.length > 0 &&
      influencerData.some((row) =>
        Object.values(row).some(
          (link) => typeof link === "string" && link.trim() !== "",
        ),
      ),
    [influencerData],
  );

  const hasInvalidLinks = linkValidation.invalidLinks.size > 0;
  const hasDuplicateLinks = linkValidation.duplicateLinks.size > 0;

  // Token management
  const {
    selectedToken,
    effectiveToken,
    isLaunchDisabled: isLaunchDisabledByToken,
    handleTokenSelect,
    trackUsage,
    availableTokens,
    tokensLoading,
  } = useTokenManagement({
    apiToken: apiToken ?? "",
    isApiTokenLoading,
    user,
    showTokenDropdown: true,
    additionalValidation: () => hasLinks,
    monitoringType: "influencer",
  });

  const isLaunchDisabled =
    isLaunchDisabledByToken || hasInvalidLinks || hasDuplicateLinks;

  // API endpoint generation
  const apiEndpoint = useMemo(() => {
    if (!hasLinks) return "";
    return generateInfluencerEndpoint(
      influencerData.flatMap((row) =>
        Object.values(row).filter(
          (link): link is string =>
            typeof link === "string" && link.trim() !== "",
        ),
      ),
      state.limit,
      1,
    );
  }, [hasLinks, influencerData, state.limit]);

  // Track token usage
  useEffect(() => {
    if (matchedInfluencers && matchedInfluencers.length > 0 && !isFetching) {
      trackUsage(matchedInfluencers.length);
    }
  }, [matchedInfluencers, isFetching, trackUsage]);

  // Launch handler
  const handleLaunch = useCallback(async () => {
    await baseLaunch(refetch);
  }, [baseLaunch, refetch]);

  // File upload handler
  const handleUpload = useCallback(
    (file: File) => {
      uploadInfluencersFile(file);
    },
    [uploadInfluencersFile],
  );

  return {
    // State
    state,
    influencerData,
    matchedInfluencers,
    isFetching,
    showResults,
    hasLaunched,
    isEmptyResults,
    responseJSON,
    apiEndpoint,
    hasLinks,
    showLinks,
    linkValidation,

    // Token
    selectedToken,
    effectiveToken,
    isLaunchDisabled,
    apiToken,
    isApiTokenLoading,
    tokenSelectedPlatforms,
    availableTokens,
    tokensLoading,

    // Handlers
    handleShowResults,
    handleLaunch,
    handleCopyCode,
    handleTokenSelect,
    handlePanelResize,
    handleUpload,
    setShowLinks,
    refetch,

    // Constants
    monitoringType: MONITORING_TYPES.INFLUENCER,
  };
};
